// Client-side API calls and React hooks for image slots.
// Reads: public (no auth needed).
// Writes: require admin token from sessionStorage (set after admin-auth login).

const API_BASE = import.meta.env.DEV ? 'http://localhost:3001' : '';

export interface ImageSlot {
  slot_id: string;
  page_section: string;
  label: string;
  image_url: string | null;
  alt_text: string;
  focal_x: number;
  focal_y: number;
  zoom: number;
  aspect_ratio: string;
  object_fit: string;
  container_size: string;
  updated_at: string;
}

// ── Admin token (set by AdminLoginGate after successful auth) ─────────────────
const TOKEN_KEY = 'gfs_admin_token';

export function setAdminToken(token: string) {
  sessionStorage.setItem(TOKEN_KEY, token);
}

export function getAdminToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

function authHeaders(): Record<string, string> {
  const token = sessionStorage.getItem(TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── In-memory cache ───────────────────────────────────────────────────────────
let _cache: ImageSlot[] | null = null;
let _cacheExpiry = 0;
const CACHE_TTL = 60_000;

const _refreshListeners = new Set<() => void>();

export function invalidateSlotCache() {
  _cache = null;
  _cacheExpiry = 0;
  _refreshListeners.forEach(cb => cb());
}

export async function fetchImageSlots(): Promise<ImageSlot[]> {
  if (_cache && Date.now() < _cacheExpiry) return _cache;
  try {
    const res = await fetch(`${API_BASE}/api/cms/image-slots`);
    if (!res.ok) throw new Error('fetch failed');
    const { slots } = await res.json() as { slots: ImageSlot[] };
    _cache = slots;
    _cacheExpiry = Date.now() + CACHE_TTL;
    return slots;
  } catch {
    return _cache ?? [];
  }
}

export async function saveImageSlot(
  slotId: string,
  updates: Partial<Omit<ImageSlot, 'slot_id' | 'updated_at'>>,
): Promise<ImageSlot> {
  const res = await fetch(`${API_BASE}/api/cms/image-slots/${encodeURIComponent(slotId)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(updates),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? 'Save failed');
  }
  const { slot } = await res.json() as { slot: ImageSlot };
  invalidateSlotCache();
  return slot;
}

export function isVideoUrl(url: string): boolean {
  return /\.(mp4|mov|webm)(\?.*)?$/i.test(url);
}

/**
 * Uploads a file to Supabase Storage via a server-generated signed URL.
 * The file bytes go directly browser → Supabase (never through Vercel),
 * so there is no 4.5 MB body limit on file size.
 */
export async function uploadImageFile(slotId: string, file: File): Promise<string> {
  const imageTypes = ['image/jpeg', 'image/png', 'image/webp'];
  const videoTypes = ['video/mp4', 'video/quicktime', 'video/webm'];
  const allowed = [...imageTypes, ...videoTypes];
  if (!allowed.includes(file.type)) throw new Error('Only JPG, PNG, WebP images and MP4/MOV/WebM videos are allowed');
  const isVideo = videoTypes.includes(file.type);
  const maxSize = isVideo ? 100 * 1024 * 1024 : 50 * 1024 * 1024;
  if (file.size > maxSize) throw new Error(isVideo ? 'Video too large — max 100 MB' : 'Image too large — max 50 MB');

  // Step 1: get a signed upload URL from the server
  const urlRes = await fetch(`${API_BASE}/api/cms/image-slots/upload-url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ slotId, mimeType: file.type }),
  });
  if (!urlRes.ok) {
    const err = await urlRes.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? 'Failed to get upload URL');
  }
  const { signedUrl, publicUrl } = await urlRes.json() as { signedUrl: string; path: string; publicUrl: string };

  // Step 2: PUT the file directly to Supabase Storage (no Vercel involvement)
  const uploadRes = await fetch(signedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!uploadRes.ok) throw new Error(`Storage upload failed: ${uploadRes.status}`);

  return publicUrl;
}

// ── React hooks ───────────────────────────────────────────────────────────────
import { useState, useEffect, useCallback } from 'react';

export function useImageSlots() {
  const [slots, setSlots] = useState<ImageSlot[]>(_cache ?? []);
  const [loading, setLoading] = useState(!_cache);
  const [error, setError] = useState<string | null>(null);

  const doFetch = useCallback(() => {
    setLoading(true);
    fetchImageSlots()
      .then(s => { setSlots(s); setLoading(false); })
      .catch(e => { setError(String(e)); setLoading(false); });
  }, []);

  useEffect(() => {
    doFetch();
    _refreshListeners.add(doFetch);
    return () => { _refreshListeners.delete(doFetch); };
  }, [doFetch]);

  const refetch = () => invalidateSlotCache();
  return { slots, loading, error, refetch };
}

export function useImageSlot(slotId: string) {
  const { slots, loading } = useImageSlots();
  return { slot: slots.find(s => s.slot_id === slotId) ?? null, loading };
}

// ── Gallery slot utilities ────────────────────────────────────────────────────
const GALLERY_SEP = '__g';

export function gallerySlotPrefix(parentSlotId: string) {
  return `${parentSlotId}${GALLERY_SEP}`;
}

export function getGallerySlots(slots: ImageSlot[], parentSlotId: string): ImageSlot[] {
  const prefix = gallerySlotPrefix(parentSlotId);
  return slots
    .filter(s => s.slot_id.startsWith(prefix) && /^\d+$/.test(s.slot_id.slice(prefix.length)))
    .sort((a, b) => {
      const na = parseInt(a.slot_id.slice(prefix.length), 10);
      const nb = parseInt(b.slot_id.slice(prefix.length), 10);
      return na - nb;
    });
}

export function isGallerySlot(slotId: string) {
  return slotId.includes(GALLERY_SEP);
}

export function nextGallerySlotId(slots: ImageSlot[], parentSlotId: string): string {
  const existing = getGallerySlots(slots, parentSlotId);
  const prefix = gallerySlotPrefix(parentSlotId);
  const maxN = existing.reduce((m, s) => {
    const n = parseInt(s.slot_id.slice(prefix.length), 10);
    return isNaN(n) ? m : Math.max(m, n);
  }, 0);
  return `${prefix}${maxN + 1}`;
}

export async function createImageSlot(
  slot_id: string,
  image_url: string,
  alt_text = '',
  page_section = '',
  label = '',
): Promise<ImageSlot> {
  const res = await fetch(`${API_BASE}/api/cms/image-slots`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ slot_id, image_url, alt_text, page_section, label }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? 'Create failed');
  }
  const { slot } = await res.json() as { slot: ImageSlot };
  invalidateSlotCache();
  return slot;
}

export async function deleteImageSlot(slotId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/cms/image-slots/${encodeURIComponent(slotId)}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? 'Delete failed');
  }
  invalidateSlotCache();
}

export function useGallerySlots(parentSlotId: string) {
  const { slots, loading } = useImageSlots();
  return { gallerySlots: getGallerySlots(slots, parentSlotId), loading };
}
